/**
 * FeatureGate — wraps a route element and renders an upgrade paywall when
 * the current tenant's plan doesn't include the required capability.
 *
 * Reads `user.plan_features[feature]` (backend `user_public` payload).
 * Master/admin roles are always treated as enabled.
 */
import { Navigate } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";

const FEATURE_LABEL = {
  client_debts: { es: "Deudas Clientes", en: "Client debts" },
  budget: { es: "Presupuesto", en: "Budget" },
  menu: { es: "Carta y Escandallos", en: "Menu & recipe costing" },
};

export function useFeature(feature) {
  const { user } = useAuth();
  if (!user) return false;
  if (["master", "admin"].includes(user.role)) return true;
  return !!(user.plan_features && user.plan_features[feature]);
}

export default function FeatureGate({ feature, children }) {
  const { user, loading } = useAuth();
  const { lang } = useI18n();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const isPrivileged = ["master", "admin"].includes(user.role);
  const enabled = isPrivileged || !!(user.plan_features && user.plan_features[feature]);
  if (enabled) return children;

  const label = FEATURE_LABEL[feature]?.[lang] || feature;

  return (
    <div
      className="max-w-lg mx-auto mt-16 text-center p-8 rounded-lg border border-[var(--border-color)] bg-[var(--surface)]"
      data-testid={`feature-paywall-${feature}`}
    >
      <div className="w-14 h-14 rounded-full bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] grid place-items-center mx-auto mb-4">
        <Lock size={24} strokeWidth={1.75} />
      </div>
      <h1 className="font-display text-2xl mb-2 tracking-tight">
        {lang === "es" ? `${label} está en Pro` : `${label} is on Pro`}
      </h1>
      <p className="text-sm text-[var(--text-secondary)] mb-6">
        {lang === "es"
          ? `Tu plan actual (${user.plan}) no incluye ${label.toLowerCase()}. Actualiza a Pro o Business para desbloquearlo.`
          : `Your current plan (${user.plan}) doesn't include ${label.toLowerCase()}. Upgrade to Pro or Business to unlock it.`}
      </p>
      <Button
        onClick={() => { window.location.href = "/settings"; }}
        className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/90 text-white"
        data-testid={`feature-paywall-upgrade-${feature}`}
      >
        <Sparkles size={14} className="mr-1.5" />
        {lang === "es" ? "Ver planes" : "See plans"}
      </Button>
    </div>
  );
}
